import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { RefreshCcw, Flag } from "lucide-react";
import { toast } from "sonner";
import AdminGate, { adminAuthHeaders } from "@/components/AdminGate";
import { fadeInUp, transition } from "@/utils/motion";

const BADGE_STYLES = {
  new: "bg-yellow-500 text-black",
  reviewed: "bg-blue-500 text-black",
  resolved: "bg-green-500 text-black",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsContent = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API}/stratagems/reports`);
      setReports(response.data || []);
    } catch {
      setError("Unable to load reports.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const filtered = useMemo(() => {
    if (filter === "all") {
      return reports;
    }
    return reports.filter((report) => report.status === filter);
  }, [reports, filter]);

  const updateStatus = async (reportId, status) => {
    try {
      await axios.patch(
        `${API}/stratagems/reports/${reportId}`,
        { status },
        { headers: adminAuthHeaders() },
      );
      toast.success("Report updated.");
      fetchReports();
    } catch (err) {
      toast.error("Unable to update report.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 md:px-12" data-testid="reports-page">
      <motion.section
        {...fadeInUp}
        transition={transition}
        className="hud-card clip-corners p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1
              className="font-headings text-4xl uppercase tracking-widest text-[#FFE800]"
              data-testid="reports-title"
            >
              Incoming Reports
            </h1>
            <p className="text-sm text-gray-400" data-testid="reports-subtitle">
              User-submitted reports for incorrect stratagem data.
            </p>
          </div>
          <button
            onClick={fetchReports}
            className="flex items-center gap-2 border border-[#FFE800]/40 px-4 py-2 text-xs font-data text-[#FFE800] hover:border-[#FFE800]"
            data-testid="reports-refresh-button"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="reports-filter">
          <button
            onClick={() => setFilter("all")}
            className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
              filter === "all"
                ? "border-[#FFE800] text-[#FFE800]"
                : "border-white/10 text-gray-400 hover:text-[#FFE800]"
            }`}
            data-testid="reports-filter-all"
          >
            All ({reports.length})
          </button>
          <button
            onClick={() => setFilter("new")}
            className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
              filter === "new"
                ? "border-[#FFE800] text-[#FFE800]"
                : "border-white/10 text-gray-400 hover:text-[#FFE800]"
            }`}
            data-testid="reports-filter-new"
          >
            New ({reports.filter((r) => r.status === "new").length})
          </button>
          <button
            onClick={() => setFilter("reviewed")}
            className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
              filter === "reviewed"
                ? "border-[#FFE800] text-[#FFE800]"
                : "border-white/10 text-gray-400 hover:text-[#FFE800]"
            }`}
            data-testid="reports-filter-reviewed"
          >
            Reviewed ({reports.filter((r) => r.status === "reviewed").length})
          </button>
          <button
            onClick={() => setFilter("resolved")}
            className={`border px-3 py-2 text-xs font-headings uppercase tracking-[0.2em] ${
              filter === "resolved"
                ? "border-[#FFE800] text-[#FFE800]"
                : "border-white/10 text-gray-400 hover:text-[#FFE800]"
            }`}
            data-testid="reports-filter-resolved"
          >
            Resolved ({reports.filter((r) => r.status === "resolved").length})
          </button>
        </div>

        {loading && (
          <p className="mt-4 text-sm text-gray-400" data-testid="reports-loading">
            Loading reports...
          </p>
        )}
        {error && (
          <p className="mt-4 text-sm text-red-400" data-testid="reports-error">
            {error}
          </p>
        )}
      </motion.section>

      <div className="mt-8 space-y-4" data-testid="reports-list">
        {filtered.length === 0 && !loading && (
          <p className="text-sm text-gray-400" data-testid="reports-empty">
            No reports found.
          </p>
        )}
        {filtered.map((report) => (
          <div
            key={report.id}
            className="hud-card clip-corners p-5"
            data-testid={`reports-card-${report.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Flag className="h-4 w-4 text-red-400" />
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                    {report.stratagem_name || report.stratagem_id}
                  </p>
                  <span
                    className={`ml-2 px-2 py-0.5 text-[10px] font-bold rounded ${BADGE_STYLES[report.status] || BADGE_STYLES.new}`}
                  >
                    {(report.status || "new").toUpperCase()}
                  </span>
                </div>
                <p className="mt-2 text-white">{report.reason}</p>
                <p className="mt-2 text-xs text-gray-500">
                  Reported: {new Date(report.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {report.status === "new" && (
                  <button
                    onClick={() => updateStatus(report.id, "reviewed")}
                    className="flex items-center gap-2 border border-green-500/40 px-3 py-2 text-xs font-data text-green-300 hover:border-green-500"
                    data-testid={`reports-review-${report.id}`}
                  >
                    Mark reviewed
                  </button>
                )}
                {report.status === "reviewed" && (
                  <>
                    <button
                      onClick={() => updateStatus(report.id, "new")}
                      className="flex items-center gap-2 border border-yellow-500/40 px-3 py-2 text-xs font-data text-yellow-300 hover:border-yellow-500"
                      data-testid={`reports-reopen-${report.id}`}
                    >
                      Reopen
                    </button>
                    <button
                      onClick={() => updateStatus(report.id, "resolved")}
                      className="flex items-center gap-2 border border-blue-500/40 px-3 py-2 text-xs font-data text-blue-300 hover:border-blue-500"
                      data-testid={`reports-resolve-${report.id}`}
                    >
                      Resolve
                    </button>
                  </>
                )}
                {report.status === "resolved" && (
                  <button
                    onClick={() => updateStatus(report.id, "new")}
                    className="flex items-center gap-2 border border-yellow-500/40 px-3 py-2 text-xs font-data text-yellow-300 hover:border-yellow-500"
                    data-testid={`reports-reopen-resolved-${report.id}`}
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Reports = ({ embedded }) => {
  if (embedded) return <ReportsContent />;
  return (
    <AdminGate>
      <ReportsContent />
    </AdminGate>
  );
};

export default Reports;
